import { StatusHistoryItem } from 'types/contact.type';
import { CallDirectionType, DispositionType } from 'types/call.type';
import { SMSDirectionType } from 'types/sms.type';

export type ProcessedCallHistory = {
    id: string;
    date: Date;
    duration: string;
    status: string;
    workflow: string;
    workflowId: string;
    callDirection?: CallDirectionType;
    disposition?: DispositionType;
    aggregatedTagsUsed?: boolean;
    type: 'call';
};

export type ProcessedSMSHistory = {
    id: string;
    date: Date;
    message: string;
    status: string;
    workflow: string;
    workflowId: string;
    messageDirection?: SMSDirectionType;
    disposition?: DispositionType;
    type: 'sms';
};

/** Discriminated union — interfaces cannot extend `A | B`. */
export type ProcessedHistory = ProcessedCallHistory | ProcessedSMSHistory;

// Helper function to calculate duration between two timestamps
export const calculateDuration = (startTime?: string, endTime?: string): string => {
    if (!startTime || !endTime) return '0:00';

    const start = new Date(startTime);
    const end = new Date(endTime);

    const durationMs = end.getTime() - start.getTime();
    if (durationMs <= 0) return '0:00';

    const seconds = Math.floor((durationMs / 1000) % 60);
    const minutes = Math.floor((durationMs / (1000 * 60)) % 60);

    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

export interface ProcessedStatusHistory {
    id: string;
    date: Date;
    status: string;
    changedBy: string;
    note: string;
    workflowId: string;
    workflowName: string;
}

// Process history data to only show status changes
export const processStatusHistory = (historyData?: StatusHistoryItem[]): ProcessedStatusHistory[] => {
    if (!historyData?.length) return [];

    const sortedHistory = [...historyData].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );

    const statusChanges: ProcessedStatusHistory[] = [];
    const currentStatusByWorkflow: Record<string, string | null> = {};

    sortedHistory.forEach((item) => {
        const workflowId = item.workflow_id || 'unknown';
        const status = item.party_stage;
        const currentStatus = currentStatusByWorkflow[workflowId] ?? null;

        if (status !== currentStatus) {
            statusChanges.push({
                id: item.id,
                date: new Date(item.created_at),
                status: status ? status.replace(/_/g, ' ') : 'Unknown',
                changedBy: item.changed_by || 'System',
                note: item.note || '',
                workflowId,
                workflowName: item.workflow_name || workflowId,
            });

            currentStatusByWorkflow[workflowId] = status;
        }
    });

    return statusChanges;
};

export type ProcessedWorkflowHistoryGroup = {
    workflowId: string;
    workflowName: string;
    partyStage?: string;
    lastDisposition?: DispositionType;
    items: ProcessedHistory[];
};

const getLastDisposition = (items: ProcessedHistory[]): DispositionType | undefined => {
    return items.find((item) => item.disposition)?.disposition;
};

export const getWorkflowPartyStages = (historyData?: StatusHistoryItem[]): Record<string, string> => {
    if (!historyData?.length) return {};

    const sortedHistory = [...historyData].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );

    return sortedHistory.reduce<Record<string, string>>((partyStagesByWorkflow, item) => {
        if (!item.workflow_id || partyStagesByWorkflow[item.workflow_id]) {
            return partyStagesByWorkflow;
        }

        partyStagesByWorkflow[item.workflow_id] = item.party_stage;
        return partyStagesByWorkflow;
    }, {});
};

export const groupHistoryByWorkflow = (
    history: ProcessedHistory[],
    workflowPartyStages: Record<string, string> = {}
): ProcessedWorkflowHistoryGroup[] => {
    const groupsMap = new Map<string, ProcessedWorkflowHistoryGroup>();

    history.forEach((item) => {
        const existingGroup = groupsMap.get(item.workflowId);

        if (existingGroup) {
            existingGroup.items.push(item);
            return;
        }

        groupsMap.set(item.workflowId, {
            workflowId: item.workflowId,
            workflowName: item.workflow,
            items: [item],
        });
    });

    return Array.from(groupsMap.values())
        .map((group) => {
            const sortedItems = [...group.items].sort((a, b) => b.date.getTime() - a.date.getTime());

            return {
                ...group,
                items: sortedItems,
                partyStage: workflowPartyStages[group.workflowId],
                lastDisposition: getLastDisposition(sortedItems),
            };
        })
        .sort((a, b) => {
            const aLatestDate = a.items[0]?.date.getTime() ?? 0;
            const bLatestDate = b.items[0]?.date.getTime() ?? 0;
            return bLatestDate - aLatestDate;
        });
};

export type ProcessedStatusWorkflowGroup = {
    workflowId: string;
    workflowName: string;
    partyStage?: string;
    items: ProcessedStatusHistory[];
};

export const groupStatusHistoryByWorkflow = (
    history: ProcessedStatusHistory[],
    workflowPartyStages: Record<string, string> = {}
): ProcessedStatusWorkflowGroup[] => {
    const groupsMap = new Map<string, ProcessedStatusWorkflowGroup>();

    history.forEach((item) => {
        const existingGroup = groupsMap.get(item.workflowId);

        if (existingGroup) {
            existingGroup.items.push(item);
            return;
        }

        groupsMap.set(item.workflowId, {
            workflowId: item.workflowId,
            workflowName: item.workflowName,
            items: [item],
        });
    });

    return Array.from(groupsMap.values())
        .map((group) => {
            const sortedItems = [...group.items].sort((a, b) => b.date.getTime() - a.date.getTime());

            return {
                ...group,
                items: sortedItems,
                partyStage: workflowPartyStages[group.workflowId],
            };
        })
        .sort((a, b) => {
            const aLatestDate = a.items[0]?.date.getTime() ?? 0;
            const bLatestDate = b.items[0]?.date.getTime() ?? 0;
            return bLatestDate - aLatestDate;
        });
};
